/**
 * IMAAAAAAAGE SLICEEEEEEERRR!!
 *
 * Every beginning
 * has an end
 * ...
 * and sometimes a middle
 */
import React from 'react';
import SHARED_CONST from '../backAndFront/constant';
// The editor's own stylesheets, imported HERE so they stay in main.css at the place they always
// had: before the auth modals' and App.scss. Left to the editor's chunk they would load AFTER
// App.scss, and every tie between the two (same specificity, same property) would change
// winner — invisible to verify:all. This keeps main.css byte-identical; it costs other pages
// ~2 KB gzip. A NEW editor stylesheet can simply live in the editor's chunk: it has no old
// position to keep.
import './components/SliceTransformToolbar.scss';
import './components/LayersDrawer.scss';
import './components/CanvasDrawer.scss';
import './components/SliceDrawer.scss';
import './components/Tooltip.scss';
import AuthLauncher from './components/AuthLauncher';
import './components/AppErrorBanner.scss';
import { Help, PAGES, SlicePage } from './routes';
import './App.scss';

// Admin-only views are lazy-loaded so their code (and the /api/admin/* calls they
// make) ships in SEPARATE chunks, fetched only when an admin actually opens the
// page — never bundled into the main.js that every visitor downloads.
const Admin = React.lazy(() => import(/* webpackChunkName: "admin" */ './views/Admin'));

// The editor is its own chunk too. It is most of the app's code, and a visitor who opens a
// shared `/s/:id` link or any other page has no use for it.
const EditorPage = React.lazy(() => import(/* webpackChunkName: "editor" */ './EditorPage'));

// Lightweight top-level router: keep `/my-slices` as a deep link without pulling in react-router.
// This keeps branding/auth provider behavior consistent across pages.
export default function App() {
  const pathname = (typeof window !== 'undefined' && window.location?.pathname) ? window.location.pathname : '/';
  const search = (typeof window !== 'undefined' && window.location?.search) ? window.location.search : '';
  let page: React.ReactNode = null;
  // `/meow-mix` is the original standalone page, kept as a short URL and redirected to the admin
  // tab that renders the same panel. Two wrappers meant two cascades: `.page-admin .btn`
  // (Admin.scss) tied with `.meowmix-panel .btn--active` and won on import order, so the selected
  // tier looked unselected in the tab and correct standalone. One rendering context, one answer.
  if (pathname === '/meow-mix') {
    try {
      window.location.replace('/_su#meowmix');
    } catch (_e) {
      // ignore — the blank page below is the worst case
    }
    page = <div className="page-admin container" />;
  }
  if (pathname === '/_su') {
    page = (
      <React.Suspense fallback={ <div className="page-admin container" /> }>
        <Admin />
      </React.Suspense>
    );
  }
  const Page = PAGES[pathname];
  if (Page) page = <Page />;
  // `/help/<slug>` — one page per topic, resolved by prefix like `/s/:id`. The bare `/help`
  // index comes through PAGES above.
  const helpMatch = /^\/help\/([a-z0-9-]+)$/.exec(pathname);
  if (helpMatch) page = <Help slug={ helpMatch[1] } />;
  // Canonical public URL for saved slices is `/s/:id` (short + neutral).
  //
  // Back-compat: older links used `/slice/:id`. Keep them working by redirecting to `/s/:id`
  // while preserving query string (notably `?edit=<token>`).
  const legacySliceMatch = /^\/slice\/([^/]+)$/.exec(pathname);
  if (legacySliceMatch) {
    // Avoid a full page navigation (which looks like a flicker). We can render the page directly
    // and just rewrite the URL in-place.
    page = <SlicePage sliceId={ legacySliceMatch[1] } />;
    try {
      const next = `${SHARED_CONST.SLICE_PAGE_PATH_PREFIX}/${legacySliceMatch[1]}${search || ''}`;
      window.history.replaceState({}, '', next);
    } catch (_e) {
      // ignore
    }
  }
  const sliceMatch = /^\/s\/([^/]+)$/.exec(pathname);
  if (!page && sliceMatch) page = <SlicePage sliceId={ sliceMatch[1] } />;
  if (!page) page = <EditorPage />;
  // ONE boundary around the page AND the launcher, so a page whose code is still loading
  // (routes.tsx, or the editor) shows nothing at all rather than the launcher on its own for a
  // moment.
  return (
    <React.Suspense fallback={ null }>
      { page }
      <AuthLauncher />
    </React.Suspense>
  );
}
